import React from 'react'
import {
  FILTER_KEYS,
  buildInvoicesDeepLinkPath,
  buildReceiptingDeepLinkPath,
} from './reconciliationHealth'
import {
  countOpenCreditAsksFromHistory,
  countOpenCreditRequestsFromHistory,
} from './askForCreditBridgeHelpers'

export const RECEIPTING_HEALTH_BADGE_COLOR = '#fa8c16'

const VALID_FILTER_KEYS = new Set(Object.values(FILTER_KEYS))
const VALID_MATCHING_STATUSES = new Set([
  'unprocessed',
  'match_found',
  'partial_match',
  'needs_action',
  'possible_duplicate',
  'no_match',
  'multiple_matches',
])

export function parseReceiptingFilterFromSearch(search = '') {
  const query = search.startsWith('?') ? search.slice(1) : search
  const filter = new URLSearchParams(query).get('filter')
  if (filter && VALID_FILTER_KEYS.has(filter)) {
    return filter
  }
  return null
}

export function parseReceiptingSearchQueryFromSearch(search = '') {
  const query = search.startsWith('?') ? search.slice(1) : search
  return new URLSearchParams(query).get('q') || null
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Optional start/end (YYYY-MM-DD) from Ollie / health deep-links so the
 * referenced PO is inside the Goods Receipting date window.
 */
export function parseReceiptingDateRangeFromSearch(search = '') {
  const query = search.startsWith('?') ? search.slice(1) : search
  const params = new URLSearchParams(query)
  const start = String(params.get('start') || '').trim()
  const end = String(params.get('end') || '').trim()
  if (!ISO_DAY.test(start) || !ISO_DAY.test(end)) return null
  if (start > end) return null
  return { start, end }
}

export function parseInvoicesMatchingFromSearch(search = '') {
  const query = search.startsWith('?') ? search.slice(1) : search
  const raw = new URLSearchParams(query).get('matching')
  if (!raw) return []
  return raw
    .split(',')
    .map(value => value.trim())
    .filter(value => VALID_MATCHING_STATUSES.has(value))
}

export function parseInvoicesSearchQueryFromSearch(search = '') {
  const query = search.startsWith('?') ? search.slice(1) : search
  return new URLSearchParams(query).get('q') || null
}

export function buildReceiptingPath(venueId, filter, { q } = {}) {
  return buildReceiptingDeepLinkPath(venueId, { filter, q })
}

export function buildInvoicesPath(
  venueId,
  {
    matching,
    q,
    invoiceId,
    upload,
    po,
    supplierId,
    supplierTitle,
    review,
    openInWorkspace,
  } = {},
) {
  return buildInvoicesDeepLinkPath(venueId, {
    matching,
    q,
    invoiceId,
    upload,
    po,
    supplierId,
    supplierTitle,
    review,
    openInWorkspace,
  })
}

/** Ollie / health deep-link: apply Match queue “Needs my review”. */
export function parseInvoicesReviewFromSearch(search = '') {
  const query = search.startsWith('?') ? search.slice(1) : search
  return new URLSearchParams(query).get('review') === '1'
}

/** Ollie deep-link: open this invoice in the Match workspace. */
export function parseInvoicesInvoiceIdFromSearch(search = '') {
  const query = search.startsWith('?') ? search.slice(1) : search
  const id = new URLSearchParams(query).get('invoiceId')
  return id && String(id).trim() ? String(id).trim() : null
}

/** Open Credit Requests (Ask-for-credit) list. */
export function buildCreditRequestsPath(venueId) {
  if (!venueId) return '/purchasing/creditnotes'
  return `/v/${venueId}/purchasing/creditnotes`
}

export function parseInvoicesUploadPrefillFromSearch(search = '') {
  const query = search.startsWith('?') ? search.slice(1) : search
  const params = new URLSearchParams(query)
  const upload = params.get('upload') === '1'
  if (!upload) {
    return { upload: false, po: '', supplierId: '', supplierTitle: '' }
  }
  return {
    upload: true,
    po: String(params.get('po') || '').trim(),
    supplierId: String(params.get('supplierId') || '').trim(),
    supplierTitle: String(params.get('supplier') || '').trim(),
  }
}

export function formatHealthSummaryDateRange(dateRange) {
  if (!dateRange?.start || !dateRange?.end) return null
  const formatIso = iso => {
    const [year, month, day] = String(iso).split('-')
    if (!year || !month || !day) return iso
    return `${day}/${month}/${year}`
  }
  return `${formatIso(dateRange.start)} – ${formatIso(dateRange.end)}`
}

export function buildReceiptingHealthTooltip(needsAction, dateRangeLabel) {
  const count = Number(needsAction) || 0
  if (count <= 0) return ''
  const orderLabel = `${count} order${count === 1 ? '' : 's'} need${
    count === 1 ? 's' : ''
  } reconciliation`
  if (dateRangeLabel) {
    return `${orderLabel} (${dateRangeLabel})`
  }
  return orderLabel
}

export function buildInvoiceMatchingWorkTooltip(matchingWorkCount) {
  const count = Number(matchingWorkCount) || 0
  if (count <= 0) return ''
  return `${count} invoice${count === 1 ? '' : 's'} need${
    count === 1 ? 's' : ''
  } matching`
}

export function shouldShowReceiptingHealthBadge(count) {
  return Number(count) > 0
}

export function shouldShowInvoiceMatchingWorkBadge(count) {
  return Number(count) > 0
}

export async function fetchReconciliationHealthSummary({
  venueId,
  apiGet,
  start,
  end,
}) {
  if (!venueId) {
    return {
      needsAction: 0,
      matchingWorkCount: 0,
      openCreditRequestCount: 0,
      total: 0,
      dateRange: null,
      dateRangeLabel: null,
    }
  }

  const queryStringParameters = {}
  if (start) queryStringParameters.start = start
  if (end) queryStringParameters.end = end

  const response = await apiGet(
    'api2',
    `receipting/${venueId}/health-summary`,
    {
      queryStringParameters,
    },
  )

  if (!response?.success) {
    return {
      needsAction: 0,
      matchingWorkCount: 0,
      openCreditRequestCount: 0,
      total: 0,
      dateRange: null,
      dateRangeLabel: null,
    }
  }

  const dateRangeLabel = formatHealthSummaryDateRange(response.dateRange)
  const matchingWorkCount =
    Number(response.invoiceHealth?.matchingWorkCount) || 0

  return {
    needsAction: response.needsAction || 0,
    matchingWorkCount,
    // Credit Requests badge uses credits-history open lines (see
    // fetchOpenCreditRequestCount) — not GR creditWorkflow PO count.
    openCreditRequestCount: 0,
    total: response.total || 0,
    reconciled: response.reconciled || 0,
    percentReconciled: response.percentReconciled || 0,
    dateRange: response.dateRange || null,
    dateRangeLabel,
    invoiceHealth: response.invoiceHealth || null,
    creditWorkflow: Number(response.creditWorkflow) || 0,
  }
}

/**
 * Outstanding Credit Requests tab badge count.
 * Same source as Open requests on /purchasing/creditnotes — not GR
 * creditWorkflow (PO receiptingStatus), which can be 0 while lines are open.
 */
export async function fetchOpenCreditRequestCount({ venueId, apiGet }) {
  const { newSinceCutoff } = await fetchOpenCreditRequestCounts({
    venueId,
    apiGet,
  })
  return newSinceCutoff
}

/**
 * `open` = every open credit ask (the Open requests tab rows);
 * `newSinceCutoff` = the amber badge count.
 */
export async function fetchOpenCreditRequestCounts({ venueId, apiGet }) {
  if (!venueId || typeof apiGet !== 'function') {
    return { open: 0, newSinceCutoff: 0 }
  }
  try {
    const result = await apiGet(
      'api',
      `receipting/${venueId}/credits-history?page=1&limit=250`,
    )
    const rows = result?.ordersArr || []
    return {
      open: countOpenCreditAsksFromHistory(rows),
      newSinceCutoff: countOpenCreditRequestsFromHistory(rows),
    }
  } catch (error) {
    console.error('fetchOpenCreditRequestCount failed', error)
    return { open: 0, newSinceCutoff: 0 }
  }
}

/**
 * Health panel “Credit requests” chip count — must match the Credit Requests
 * tab / nav badge (open Ask-for-credit lines), never PO creditWorkflow alone.
 */
export function resolveHealthPanelCreditRequestCount({
  openCreditRequestCount = 0,
} = {}) {
  return Math.max(0, Number(openCreditRequestCount) || 0)
}

/** One spoken count, not ant Badge digit soup (`0 1 2 3…`). */
export function formatPurchasingNavA11yName(label, count) {
  const n = Math.max(0, Number(count) || 0)
  const title = String(label || '').trim()
  if (!title) return n > 0 ? String(n) : ''
  if (n <= 0) return title
  return `${title}, ${n}`
}

function purchasingNavTabLabelWithBadge(label, count) {
  const n = Math.max(0, Number(count) || 0)
  return (
    <span aria-label={formatPurchasingNavA11yName(label, n)}>
      {label}
      <span
        aria-hidden='true'
        data-testid='purchasing-nav-badge'
        style={{
          display: 'inline-block',
          marginLeft: 8,
          minWidth: 20,
          padding: '0 6px',
          borderRadius: 10,
          backgroundColor: RECEIPTING_HEALTH_BADGE_COLOR,
          color: '#fff',
          fontSize: 12,
          lineHeight: '20px',
          textAlign: 'center',
          fontWeight: 500,
        }}
      >
        {n}
      </span>
    </span>
  )
}

export function ReceiptingHealthTabLabel({ label, needsActionCount }) {
  if (!shouldShowReceiptingHealthBadge(needsActionCount)) {
    return label
  }

  return purchasingNavTabLabelWithBadge(label, needsActionCount)
}

export function InvoiceMatchingWorkTabLabel({ label, matchingWorkCount }) {
  if (!shouldShowInvoiceMatchingWorkBadge(matchingWorkCount)) {
    return label
  }

  return purchasingNavTabLabelWithBadge(label, matchingWorkCount)
}

export function shouldShowCreditRequestHealthBadge(count) {
  return Number(count) > 0
}

export function CreditRequestHealthTabLabel({ label, openCount }) {
  if (!shouldShowCreditRequestHealthBadge(openCount)) {
    return label
  }

  return purchasingNavTabLabelWithBadge(label, openCount)
}
